import { createReducer, on } from '@ngrx/store';
import {
  IDisposition,
  IDispositionListFilter,
  IDispositionListSort,
  IDispositionName,
} from '../../models/disposition.model';
import { DispositionActions } from './disposition.actions';
import { AuthActions } from '../auth/auth.actions';

export interface DispositionState {
  dispositions: IDisposition[];
  currentDisposition: IDisposition | null;
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
  filter: IDispositionListFilter;
  sort: IDispositionListSort;
  saveSuccess: boolean;
  saveFailure: boolean;
  dispositionNames?: IDispositionName[];
  allStatusDispositionNames?: IDispositionName[];
}

export const initialDispositionState: DispositionState = {
  dispositions: [],
  currentDisposition: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
  },
  filter: {},
  sort: {
    key: 'createdAt', // Or a relevant default sort key for dispositions
    order: 'desc',
  },
  saveSuccess: false,
  saveFailure: false,
  dispositionNames: [],
  allStatusDispositionNames: [],
};

export const dispositionReducer = createReducer<DispositionState>(
  initialDispositionState,
  // Load Dispositions
  on(DispositionActions.loadDispositions, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(DispositionActions.loadDispositionsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(
    DispositionActions.loadDispositionsSuccess,
    (state, { response, filter }) => ({
      ...state,
      dispositions: [...response.data.dispositions.list],
      pagination: {
        total: response.data.dispositions.total,
        page: response.data.dispositions.page,
        limit: response.data.dispositions.limit,
      },
      filter: filter ?? {},
      loading: false,
      error: null,
    })
  ),

  // Create Disposition
  on(DispositionActions.createDisposition, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(DispositionActions.createDispositionSuccess, (state, { response }) => ({
    ...state,
    dispositions: [...state.dispositions, response.data as IDisposition],
    loading: false,
    error: null,
  })),

  on(DispositionActions.createDispositionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load Single Disposition
  on(DispositionActions.loadSingleDisposition, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(
    DispositionActions.loadSingleDispositionSuccess,
    (state, { response }) => ({
      ...state,
      currentDisposition: response.data,
      loading: false,
      error: null,
    })
  ),

  on(DispositionActions.loadSingleDispositionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Update Disposition
  on(DispositionActions.updateDisposition, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(DispositionActions.updateDispositionSuccess, (state) => ({
    ...state,
    currentDisposition: null,
    loading: false,
    error: null,
  })),
  on(DispositionActions.updateDispositionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Delete Disposition
  on(DispositionActions.deleteDispositionSuccess, (state, { id }) => ({
    ...state,
    dispositions: state.dispositions.filter((loc) => loc._id !== id),
    loading: false,
    error: null,
  })),

  // Toggle Status
  on(
    DispositionActions.toggleDispositionStatusSuccess,
    (state, { response }) => ({
      ...state,
      dispositions: state.dispositions.map((loc) =>
        loc._id === response.data.updatedDisposition._id
          ? response.data.updatedDisposition
          : loc
      ),
      currentDisposition:
        state.currentDisposition?._id === response.data.updatedDisposition._id
          ? response.data.updatedDisposition
          : state.currentDisposition,
      loading: false,
      error: null,
    })
  ),
  on(
    DispositionActions.setDispositionQueryParams,
    (state, { params, body }) => ({
      ...state,
      pagination: {
        ...state.pagination,
        page: params.page ?? state.pagination.page,
        limit: params.limit ?? state.pagination.limit,
      },
      filter: body?.filter ?? state.filter,
      sort: body?.sort ?? state.sort,
    })
  ),
  // Load Disposition Names
  on(DispositionActions.loadDispositionNames, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(DispositionActions.loadDispositionNamesSuccess, (state, { response }) => ({
    ...state,
    loading: false,
    dispositionNames: response.data.dispositions || [],
  })),
  on(DispositionActions.loadDispositionNamesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load AllStatus Disposition Names
  on(DispositionActions.loadAllStatusDispositionNames, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(
    DispositionActions.loadAllStatusDispositionNamesSuccess,
    (state, { response }) => ({
      ...state,
      loading: false,
      allStatusDispositionNames: response.data.dispositions || [],
    })
  ),
  on(
    DispositionActions.loadAllStatusDispositionNamesFailure,
    (state, { error }) => ({
      ...state,
      loading: false,
      error,
    })
  ),

  // update status
  on(DispositionActions.changeDispositionStatus, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(DispositionActions.changeDispositionStatusSuccess, (state) => ({
    ...state,
    loading: false,
    error: null,
  })),
  on(DispositionActions.changeDispositionStatusFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(DispositionActions.clearSingleDisposition, (state) => ({
    ...state,
    currentDisposition: null,
  })),

  on(DispositionActions.bulkDeleteDispositions, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(DispositionActions.bulkDeleteDispositionsSuccess, (state, { ids }) => ({
    ...state,
    dispositions: state.dispositions.filter(
      (disposition) => !ids.includes(disposition._id as number)
    ),
    loading: false,
    error: null,
  })),
  on(DispositionActions.bulkDeleteDispositionsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(AuthActions.logout, () => initialDispositionState)
);

